/**
 * PaywallProduct Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore.
 * Handles PaywallProduct entity with Date↔Timestamp conversion.
 *
 * Collection path: projects/{projectId}/paywallProducts/{productId}
 *
 * CRITICAL: Uses Client SDK signature: fromFirestore(snapshot, options)
 * NOT Admin SDK signature: fromFirestore(data)
 *
 * @module firebase-core/converters
 */

import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp
} from 'firebase/firestore';
import type { PaywallProduct } from '@segmently/schema-models/payments';

/**
 * Firestore converter for PaywallProduct entity.
 *
 * Handles:
 * - toFirestore: ISO date strings → Firestore Timestamps
 * - fromFirestore: Firestore Timestamps → ISO date strings
 * - ID management: removes id in toFirestore, adds from snapshot in fromFirestore
 * - Nested preBakedPrice.bakedAt conversion
 */
export const paywallProductConverter: FirestoreDataConverter<PaywallProduct> = {
  /**
   * Converts PaywallProduct to Firestore document data.
   *
   * - Removes id (Firestore stores it in document metadata)
   * - Converts createdAt/updatedAt ISO strings to Timestamps
   * - Converts preBakedPrice.bakedAt if present
   */
  toFirestore(product: PaywallProduct): any {
    const result: any = { ...product };

    // Remove id from data (Firestore keeps it in doc metadata)
    delete result.id;

    // Convert date strings to Timestamps
    if (result.createdAt) {
      result.createdAt = Timestamp.fromDate(new Date(result.createdAt));
    }
    if (result.updatedAt) {
      result.updatedAt = Timestamp.fromDate(new Date(result.updatedAt));
    }

    // Convert nested preBakedPrice.bakedAt
    if (result.preBakedPrice?.bakedAt) {
      result.preBakedPrice = {
        ...result.preBakedPrice,
        bakedAt: Timestamp.fromDate(new Date(result.preBakedPrice.bakedAt))
      };
    }

    // Remove ALL undefined fields (Firestore doesn't accept undefined)
    Object.keys(result).forEach(key => {
      if (result[key] === undefined) {
        delete result[key];
      }
    });

    return result;
  },

  /**
   * Converts Firestore document to PaywallProduct.
   *
   * CRITICAL: Client SDK signature - must call snapshot.data(options)
   *
   * - Adds id from snapshot.id
   * - Converts Timestamps to ISO date strings
   * - Backward compatibility: defaults paymentProvider to 'stripe' for existing records
   */
  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): PaywallProduct {
    // CRITICAL: Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data, id: snapshot.id };

    // Backward compatibility: existing products without paymentProvider are Stripe
    if (!result.paymentProvider) {
      result.paymentProvider = 'stripe';
    }

    // Convert Timestamps to Date
    if (result.createdAt?.toDate) {
      result.createdAt = result.createdAt.toDate();
    } else if (typeof result.createdAt === 'string') {
      result.createdAt = new Date(result.createdAt);
    }
    if (result.updatedAt?.toDate) {
      result.updatedAt = result.updatedAt.toDate();
    } else if (typeof result.updatedAt === 'string') {
      result.updatedAt = new Date(result.updatedAt);
    }

    // Convert nested preBakedPrice.bakedAt
    if (result.preBakedPrice?.bakedAt?.toDate) {
      result.preBakedPrice = {
        ...result.preBakedPrice,
        bakedAt: result.preBakedPrice.bakedAt.toDate()
      };
    } else if (typeof result.preBakedPrice?.bakedAt === 'string') {
      result.preBakedPrice = {
        ...result.preBakedPrice,
        bakedAt: new Date(result.preBakedPrice.bakedAt)
      };
    }

    return result as PaywallProduct;
  }
};
